import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { fetchSalesOrderById } from '@/api/orders';
import { SalesOrderDetailHeader } from '@/app/(client)/orders/-components/SalesOrderDetailHeader';
import { SalesOrderDetailHeaderSkeleton } from '@/app/(client)/orders/-components/SalesOrderDetailHeaderSkeleton';
import {
  salesOrderDetailKeys,
  useSalesOrderDetail,
} from '@/app/(client)/orders/-hooks/useSalesOrderDetail';
import type { SalesOrderDetail } from '@/app/(client)/orders/-types';
import type { TabEntry } from '@/components/shared/PageTabs';
import PageTabs from '@/components/shared/PageTabs';
import { queryClient } from '@/lib/utils/queryClient';
import { useOrdersTranslator } from '../../../../-hooks/useOrdersTranslator';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/(detail)/sales-orders/$soId',
)({
  beforeLoad: ({ params, location }) => {
    const bare = `/orders/sales-orders/${params.soId}`;
    if (location.pathname === bare || location.pathname === `${bare}/`) {
      throw redirect({
        to: '/orders/sales-orders/$soId/overview',
        params: { soId: params.soId },
      });
    }
  },
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: salesOrderDetailKeys.detail(params.soId),
      queryFn: () => fetchSalesOrderById(params.soId).then(res => res.data),
    }),
  component: SalesOrderDetailLayout,
});

function SalesOrderDetailLayout() {
  const { soId } = Route.useParams();
  const { getTranslation: t } = useOrdersTranslator();
  const { data: order } = useSalesOrderDetail(soId);

  const base = `/orders/sales-orders/${soId}`;
  const tabs: TabEntry[] = [
    { key: 'overview', label: t('detail.tabOverview'), to: `${base}/overview` },
    { key: 'tracing', label: t('detail.tabTracing'), to: `${base}/tracing` },
    { key: 'products', label: t('detail.tabProducts'), to: `${base}/products` },
    {
      key: 'attachments',
      label: t('detail.tabDocuments'),
      to: `${base}/attachments`,
    },
  ];

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      {order ? (
        /* fetchSalesOrderById returns SalesOrder but the single-fetch endpoint
           returns a superset matching SalesOrderDetail at runtime */
        <SalesOrderDetailHeader order={order as unknown as SalesOrderDetail} />
      ) : (
        <SalesOrderDetailHeaderSkeleton />
      )}
      <div className='sticky top-0 z-20 bg-background'>
        <PageTabs tabs={tabs} mode='path' moreLabel={t('detail.moreTabs')} />
      </div>
      <div className='@container flex flex-col flex-1 min-h-[70dvh] p-4 sm:p-6 w-full'>
        <Outlet />
      </div>
    </div>
  );
}
